class WnProductForm extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.copy = window.WN_I18N?.product || {};
    this.localizeDescriptionToggle();
    this.form = this.querySelector('[data-type="product-form"]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.variantData = this.querySelector('[data-product-variants]');
    if (!this.form || !this.variantInput || !this.variantData) return;

    try { this.variants = JSON.parse(this.variantData.textContent || '[]'); }
    catch { this.variants = []; }

    this.groups = [...this.querySelectorAll('[data-option-position]')];
    this.price = this.querySelector('[data-price-current]');
    this.compare = this.querySelector('[data-price-compare]');
    this.stock = this.querySelector('[data-product-availability]');
    this.add = this.querySelector('[data-add-to-cart]');
    this.addText = this.querySelector('[data-add-to-cart-text]');
    this.message = this.querySelector('[data-product-message]');
    this.quantity = this.querySelector('[data-quantity-input]');
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    document.addEventListener('wn:product:select-variant', (event) => this.onExternalVariantSelection(event), { signal });

    const initial = this.variants.find((variant) => String(variant.id) === String(this.variantInput.value));
    const preserveInitialMedia = this.closest('.wn-nami-recovery') !== null && !new URLSearchParams(window.location.search).has('variant');

    if (initial) this.render(initial, false, preserveInitialMedia);
  }

  disconnectedCallback() { this.abortController?.abort(); this.ready = false; }

  selectedOptions() {
    // Some Shopify options intentionally have only one value and are therefore
    // not rendered as a visible selector. Rebuild the full option array by
    // position so hidden single-value options still participate in matching.
    const base = [...(this.currentVariant?.options || this.variants[0]?.options || [])];
    this.groups.forEach((group) => {
      const index = Math.max(0, Number(group.dataset.optionPosition || 1) - 1);
      base[index] = group.querySelector('.is-selected[data-option-value]')?.dataset.optionValue || base[index] || '';
    });
    return base;
  }

  findVariant(options) {
    return this.variants.find((variant) => Array.isArray(variant.options) && variant.options.length === options.length && variant.options.every((value, index) => value === options[index]));
  }

  findVariantByTitle(title) {
    const normalized = String(title || '').trim().toLowerCase();
    if (!normalized) return null;
    return this.variants.find((variant) => String(variant.title || '').trim().toLowerCase() === normalized || variant.options?.some((option) => String(option).trim().toLowerCase() === normalized));
  }

  onExternalVariantSelection(event) {
    const variant = this.findVariantByTitle(event.detail?.title);
    if (!variant) return;

    this.groups.forEach((group) => {
      const index = Math.max(0, Number(group.dataset.optionPosition || 1) - 1);
      const value = variant.options?.[index];
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const selected = button.dataset.optionValue === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const label = group.querySelector('[data-option-selection]');
      if (label) label.textContent = value || '';
    });

    this.render(variant, true);
    if (event.detail?.scroll === true) this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  onClick(event) {
    const recommendation = event.target.closest('[data-wn-recommendation]');
    if (recommendation) {
      event.preventDefault();
      [recommendation.dataset.optionOne, recommendation.dataset.optionTwo, recommendation.dataset.optionThree]
        .filter(Boolean)
        .forEach((value) => {
          const option = [...this.querySelectorAll('[data-option-value]')].find((candidate) => candidate.dataset.optionValue === value);
          option?.click();
        });
      recommendation.closest('details')?.removeAttribute('open');
      this.querySelector('[data-product-options]')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      return;
    }

    const option = event.target.closest('[data-option-value]');
    if (option) {
      const group = option.closest('[data-option-position]');
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const selected = button === option;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const label = group.querySelector('[data-option-selection]');
      if (label) label.textContent = option.dataset.optionValue || '';
      const variant = this.findVariant(this.selectedOptions());
      variant ? this.render(variant, true) : this.unavailable();
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton && this.quantity) {
      const current = Math.max(1, parseInt(this.quantity.value, 10) || 1);
      this.quantity.value = Math.max(1, current + Number(quantityButton.dataset.quantityChange || 0));
    }
  }

  render(variant, updateUrl, preserveMedia = false) {
    this.currentVariant = variant;
    this.variantInput.value = variant.id;
    if (this.price) this.price.textContent = variant.price_formatted || '';

    const onSale = Number(variant.compare_at_price) > Number(variant.price);
    if (this.compare) {
      this.compare.hidden = !onSale;
      this.compare.textContent = onSale ? variant.compare_at_price_formatted || '' : '';
    }

    if (this.add) this.add.disabled = !variant.available;
    if (this.addText) this.addText.textContent = variant.available ? this.add.dataset.addLabel : this.add.dataset.soldOutLabel;
    if (this.stock) {
      this.stock.textContent = variant.available ? (this.copy.available || '') : (this.copy.soldOut || this.add?.dataset.soldOutLabel || '');
      this.stock.dataset.available = String(variant.available);
    }

    this.markUnavailableOptions();

    if (updateUrl && history.replaceState) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    if (this.message) this.message.textContent = '';
    this.dispatchEvent(new CustomEvent('wn:product:variant-change', { bubbles: true, detail: { variant, preserveMedia } }));
  }

  unavailable() {
    this.currentVariant = null;
    if (this.add) this.add.disabled = true;
    if (this.addText) this.addText.textContent = this.copy.notAvailable || this.add?.dataset.soldOutLabel || '';
    if (this.stock) {
      this.stock.textContent = this.copy.combinationUnavailable || this.copy.notAvailable || '';
      this.stock.dataset.available = 'false';
    }
  }

  markUnavailableOptions() {
    const selected = this.selectedOptions();
    this.groups.forEach((group) => {
      const index = Math.max(0, Number(group.dataset.optionPosition || 1) - 1);
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const options = [...selected];
        options[index] = button.dataset.optionValue || '';
        const variant = this.findVariant(options);
        button.classList.toggle('is-sold-out', Boolean(variant && !variant.available));
      });
    });
  }

  localizeDescriptionToggle() {
    const labels = {
      nl: ['Lees meer', 'Minder tonen'],
      en: ['Read more', 'Show less'],
      de: ['Mehr lesen', 'Weniger anzeigen'],
      fr: ['Lire la suite', 'Afficher moins'],
      es: ['Leer más', 'Mostrar menos'],
      it: ['Leggi di più', 'Mostra meno']
    };
    const locale = String(document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
    const [more, less] = labels[locale] || labels.en;
    this.querySelectorAll('.wn-product-buybox__description-more-label--more').forEach((node) => { node.textContent = more; });
    this.querySelectorAll('.wn-product-buybox__description-more-label--less').forEach((node) => { node.textContent = less; });
  }

  loading(state) {
    if (!this.add || !this.addText) return;
    this.add.disabled = state || !this.currentVariant?.available;
    this.add.setAttribute('aria-busy', String(state));
    this.addText.textContent = state ? this.add.dataset.loadingLabel : this.currentVariant?.available ? this.add.dataset.addLabel : this.add.dataset.soldOutLabel;
  }

  async onSubmit(event) {
    if (!window.WNCart?.add) return;
    event.preventDefault();
    if (!this.currentVariant?.available) return;

    const variant = this.currentVariant;
    this.loading(true);
    if (this.message) { this.message.className = 'wn-product-form__message'; this.message.textContent = ''; }
    this.dispatchEvent(new CustomEvent('wn:product:add-start', { bubbles: true, detail: { variant } }));

    try {
      const formData = new FormData(this.form);
      const care = this.querySelector('[data-wn-refill-care]');
      if (care && (typeof care.getPurchaseIntent !== 'function' || typeof care.composePurchase !== 'function')) {
        throw new Error(this.copy.addError || 'De aankoopopties zijn nog niet gereed.');
      }
      const intent = care?.getPurchaseIntent?.({ formData, variant }) || null;
      let input = formData;
      let source = 'product-form';

      if (intent) {
        const currentCart = await window.WNCart.refresh({ emit: false, source: 'product-form-preflight' });
        const plan = care.composePurchase?.({ cart: currentCart, intent });
        if (!plan?.input) throw new Error(this.copy.addError || 'De aankoop kon niet worden samengesteld.');

        if (plan.updates && Object.keys(plan.updates).length) {
          await window.WNCart.update(
            { updates: plan.updates },
            { source: `${plan.source || 'product-care'}-normalize`, openDrawer: false, emit: false }
          );
        }

        input = plan.input;
        source = plan.source || 'product-care';
      }

      const { cart, item } = await window.WNCart.add(input, { source, openDrawer: true });
      if (this.message) { this.message.className = 'wn-product-form__message is-success'; this.message.textContent = this.copy.addedToCart || ''; }
      this.dispatchEvent(new CustomEvent('wn:product:add-success', { bubbles: true, detail: { cart, item, variant } }));
    } catch (error) {
      const message = error.message || this.copy.addError || '';
      if (this.message) { this.message.className = 'wn-product-form__message is-error'; this.message.textContent = message; }
      this.dispatchEvent(new CustomEvent('wn:product:add-error', { bubbles: true, detail: { error, variant } }));
    } finally { this.loading(false); }
  }
}

if (!customElements.get('wn-product-form')) customElements.define('wn-product-form', WnProductForm);
